/**
 * What a messaging account did, and what it cost — for a client about its own
 * service, and for staff about one client or all of them.
 *
 * One component for both apps because it is one report: the API sends the same
 * summary and failures whoever asks, and only the path (and so the scope)
 * differs. Every figure is the server's. Delivery rate and cost arrive as
 * strings and are shown as written, never recomputed here.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO, startOfMonth, subDays, subMonths, endOfMonth } from 'date-fns';
import { Download } from 'lucide-react';

import { api, ApiError } from '../../lib/api-client';
import { formatDate, formatMoney, formatNumber } from '../../lib/format';
import { toast } from '../ui/toast';
import { Button } from '../ui/button';
import { Input, Select } from '../ui/input';
import { StatCard, StatRow, Tabs } from '../ui/layout';
import { Card, CardBody, CardHeader } from '../ui/surfaces';
import { EmptyState, ErrorState, LoadingState } from '../states';
import { ScrollArea } from '../ui/scroll-area';

export type UsageGroup = 'day' | 'template' | 'sender' | 'client';

export interface UsageFigures {
  sends: number;
  recipients: number;
  parts: number;
  delivered: number;
  failed: number;
  /** Delivered as a share of messages with a final answer, e.g. `"96.4"`. */
  delivery_rate: string | null;
  /** Null until the gateway has reported a rate: unknown, not free. */
  cost: string | null;
}

export interface UsageRow extends UsageFigures {
  key: string;
  label: string;
}

export interface UsageSummary {
  since: string;
  until: string;
  group: UsageGroup;
  /**
   * What `parts` counts for this service — "SMS parts", "messages" (WhatsApp,
   * RCS), "emails" — or "parts" across several. Absent from an older server,
   * which only counted SMS parts.
   */
  unit?: string;
  rows: UsageRow[];
  totals: UsageFigures;
}

export interface FailureReason {
  reason: string;
  label: string;
  count: number;
  examples: string[];
}

export interface FailureReasons {
  since: string;
  until: string;
  total: number;
  reasons: FailureReason[];
}

export type PeriodPreset = '7d' | '30d' | '90d' | 'this_month' | 'last_month';

const PRESETS: { value: PeriodPreset; label: string }[] = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
];

const GROUP_LABELS: Record<UsageGroup, string> = {
  day: 'By day',
  template: 'By template',
  sender: 'By sender',
  client: 'By client',
};

/**
 * A preset as calendar days, `YYYY-MM-DD`, counted back from `today` — itself a
 * date string, so no instant and no time zone enters the arithmetic.
 */
export function periodRange(preset: PeriodPreset, today: string): { since: string; until: string } {
  const day = parseISO(today);
  const iso = (d: Date) => format(d, 'yyyy-MM-dd');
  switch (preset) {
    case '7d':
      return { since: iso(subDays(day, 6)), until: today };
    case '90d':
      return { since: iso(subDays(day, 89)), until: today };
    case 'this_month':
      return { since: iso(startOfMonth(day)), until: today };
    case 'last_month': {
      const previous = subMonths(day, 1);
      return { since: iso(startOfMonth(previous)), until: iso(endOfMonth(previous)) };
    }
    default:
      return { since: iso(subDays(day, 29)), until: today };
  }
}

export interface UsageReportProps {
  /** The reports root: `/messaging/clients/{id}/reports`, `/messaging/my-services/{id}/reports`. */
  basePath: string;
  /** Prefixes every query this report makes, so a live update can refresh it. */
  queryKey: readonly unknown[];
  groups: readonly UsageGroup[];
  /** The failures breakdown, where the path has one. */
  showFailures?: boolean;
  /** Whether the monthly statement can be downloaded here. */
  canExport?: boolean;
  /** Today where the reader is, `YYYY-MM-DD`; taken as an argument so a test can pin it. */
  today: string;
  currency?: string;
  title?: string;
}

export function UsageReport({
  basePath,
  queryKey,
  groups,
  showFailures = true,
  canExport = false,
  today,
  currency = 'INR',
  title = 'Usage and delivery',
}: UsageReportProps) {
  const [preset, setPreset] = useState<PeriodPreset>('30d');
  const [group, setGroup] = useState<UsageGroup>(groups[0] ?? 'day');
  const [month, setMonth] = useState(today.slice(0, 7));
  const [downloading, setDownloading] = useState(false);
  const range = periodRange(preset, today);

  const summary = useQuery({
    queryKey: [...queryKey, 'summary', range.since, range.until, group],
    queryFn: () => api.get<UsageSummary>(`${basePath}/summary`, { params: { ...range, group } }),
  });
  const failures = useQuery({
    queryKey: [...queryKey, 'failures', range.since, range.until],
    queryFn: () => api.get<FailureReasons>(`${basePath}/failures`, { params: range }),
    enabled: showFailures,
  });

  const download = async () => {
    setDownloading(true);
    try {
      await api.download(`${basePath}/statement`, `messages-${month}.csv`, { params: { month } });
    } catch (caught) {
      toast.error(
        'Could not download the statement',
        caught instanceof ApiError ? caught.message : undefined,
      );
    } finally {
      setDownloading(false);
    }
  };

  const totals = summary.data?.totals;
  const waiting = totals ? totals.recipients - totals.delivered - totals.failed : 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <Select
          aria-label="Period"
          options={PRESETS}
          value={preset}
          containerClassName="w-44"
          onChange={(event) => setPreset(event.target.value as PeriodPreset)}
        />
        {canExport ? (
          <div className="flex items-end gap-2">
            <Input
              type="month"
              aria-label="Statement month"
              value={month}
              max={today.slice(0, 7)}
              containerClassName="w-40"
              onChange={(event) => setMonth(event.target.value)}
            />
            <Button
              variant="secondary"
              size="sm"
              loading={downloading}
              disabled={!/^\d{4}-\d{2}$/.test(month)}
              onClick={() => void download()}
            >
              <Download className="size-3.5" />
              Statement
            </Button>
          </div>
        ) : null}
      </div>

      {summary.isLoading ? (
        <LoadingState label="Loading usage" />
      ) : summary.error || !summary.data || !totals ? (
        <ErrorState
          message={summary.error instanceof ApiError ? summary.error.message : 'The report could not be loaded.'}
          onRetry={() => void summary.refetch()}
        />
      ) : (
        <>
          <StatRow>
            <StatCard
              label="People messaged"
              value={formatNumber(totals.recipients)}
              hint={`${formatNumber(totals.sends)} send${totals.sends === 1 ? '' : 's'} · ${formatNumber(totals.parts)} ${unitOf(summary.data)}`}
            />
            <StatCard
              label="Delivery rate"
              value={totals.delivery_rate === null ? '—' : `${totals.delivery_rate}%`}
              tone="success"
              hint={
                waiting > 0
                  ? `${formatNumber(waiting)} still waiting on a report`
                  : `${formatNumber(totals.delivered)} delivered`
              }
            />
            <StatCard
              label="Not delivered"
              value={formatNumber(totals.failed)}
              tone={totals.failed ? 'danger' : 'neutral'}
            />
            <StatCard
              label="Cost"
              value={formatMoney(totals.cost, currency)}
              hint={totals.cost === null ? 'Not reported by the gateway yet' : 'As reported by the gateway'}
            />
          </StatRow>

          <Card>
            <CardHeader
              title={title}
              actions={
                groups.length > 1 ? (
                  <Tabs
                    value={group}
                    onChange={(id) => setGroup(id as UsageGroup)}
                    items={groups.map((id) => ({ id, label: GROUP_LABELS[id] }))}
                  />
                ) : null
              }
            />
            <CardBody className="p-0">
              {summary.data.rows.length === 0 ? (
                <EmptyState
                  title="Nothing sent in this period"
                  description={`${formatDate(range.since)} to ${formatDate(range.until)}`}
                />
              ) : (
                <UsageTable group={group} rows={summary.data.rows} currency={currency} unit={unitHeading(summary.data)} />
              )}
            </CardBody>
          </Card>
        </>
      )}

      {showFailures && failures.data && failures.data.total > 0 ? (
        <Card>
          <CardHeader title="Why messages were not delivered" />
          <CardBody>
            <ul className="space-y-3">
              {failures.data.reasons.map((reason) => {
                const share = (reason.count * 100) / (failures.data?.total || 1);
                return (
                  <li key={reason.reason} className="space-y-1">
                    <div className="flex items-baseline justify-between gap-3 text-sm">
                      <span className="text-heading">{reason.label}</span>
                      <span className="tabular text-muted">{formatNumber(reason.count)}</span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-danger-subtle">
                      {/* A proportion drawn, not a figure reported: the count beside it is the fact. */}
                      <div className="h-full rounded-full bg-danger" style={{ width: `${share}%` }} />
                    </div>
                    {reason.examples.length ? (
                      <p className="truncate text-2xs text-faint" title={reason.examples.join('\n')}>
                        {reason.examples.join(' · ')}
                      </p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}

/** What a summary's `parts` counts, in a sentence's words ("SMS parts"). */
function unitOf(summary: UsageSummary | undefined): string {
  return summary?.unit ?? 'SMS parts';
}

/** The same, as a column heading ("Messages"). */
function unitHeading(summary: UsageSummary | undefined): string {
  const unit = unitOf(summary);
  return unit.charAt(0).toUpperCase() + unit.slice(1);
}

function UsageTable({
  group,
  rows,
  currency,
  unit,
}: {
  group: UsageGroup;
  rows: UsageRow[];
  currency: string;
  /** What the parts column counts, as its heading. */
  unit: string;
}) {
  const heading = { day: 'Day', template: 'Template', sender: 'Sender', client: 'Client' }[group];
  return (
    <ScrollArea className="">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-line text-left text-2xs uppercase tracking-wide text-faint">
            <th className="px-4 py-2 font-medium">{heading}</th>
            <th className="px-3 py-2 text-right font-medium">Sends</th>
            <th className="px-3 py-2 text-right font-medium">People</th>
            <th className="hidden px-3 py-2 text-right font-medium sm:table-cell">{unit}</th>
            <th className="px-3 py-2 text-right font-medium">Delivered</th>
            <th className="px-3 py-2 text-right font-medium">Failed</th>
            <th className="px-3 py-2 text-right font-medium">Rate</th>
            <th className="px-4 py-2 text-right font-medium">Cost</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-b border-line last:border-0">
              <td className="max-w-56 truncate px-4 py-2 text-heading" title={row.label}>
                {group === 'day' ? formatDate(row.key) : row.label}
              </td>
              <td className="px-3 py-2 text-right tabular">{formatNumber(row.sends)}</td>
              <td className="px-3 py-2 text-right tabular">{formatNumber(row.recipients)}</td>
              <td className="hidden px-3 py-2 text-right tabular sm:table-cell">{formatNumber(row.parts)}</td>
              <td className="px-3 py-2 text-right tabular">{formatNumber(row.delivered)}</td>
              <td className="px-3 py-2 text-right tabular">{formatNumber(row.failed)}</td>
              <td className="px-3 py-2 text-right tabular">
                {row.delivery_rate === null ? '—' : `${row.delivery_rate}%`}
              </td>
              <td className="px-4 py-2 text-right tabular">{formatMoney(row.cost, currency)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </ScrollArea>
  );
}
